import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { useDevicesStore } from "../../shared/store/devices.store";
import { DevicesService } from "../../shared/services/devices.service";
import { ButtonLarge, ButtonLink, DeviceCard } from "../../shared/uikit";

export const DevicesPage = () => {
  const navigate = useNavigate();
  const devices = useDevicesStore((s) => s.devices);
  const loading = useDevicesStore((s) => s.loading);
  const error = useDevicesStore((s) => s.error);

  const fetchDevices = useCallback(async () => {
    const { setLoading, setError, setDevices } = useDevicesStore.getState();
    setLoading(true);
    setError(null);
    try {
      const result = await DevicesService.getDevices();
      setDevices(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load devices";
      setError(message);
    }
  }, []);

  // Auto-load devices on page mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>My Devices</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "var(--second-color)",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⏳</p>
          <p>Loading devices...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>My Devices</h2>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⚠️</p>
          <p style={{ color: "var(--accent-color)" }}>{error}</p>
          <ButtonLarge
            title="🔄 Retry"
            onClick={fetchDevices}
            mode="black"
            isSmall={true}
          />
        </div>
        <ButtonLink
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
        />
      </div>
    );
  }

  // Empty state
  if (devices.length === 0) {
    return (
      <div style={{ padding: "1rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2>My Devices</h2>
          <ButtonLarge
            title="🔄 Refresh"
            onClick={fetchDevices}
            mode="white"
            isSmall={true}
          />
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            border: "1px dashed var(--color-border)",
            borderRadius: "var(--radius-md)",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "32px", marginBottom: "1rem" }}>📱</p>
          <p style={{ color: "var(--second-color)" }}>
            No devices connected yet.
          </p>
          <p
            style={{
              color: "var(--second-color)",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            Devices will appear here once you log in from another device.
          </p>
        </div>
      </div>
    );
  }

  // Devices list
  return (
    <div style={{ padding: "1rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2>My Devices</h2>
        <ButtonLarge
          title="🔄 Refresh"
          onClick={fetchDevices}
          mode="white"
          isSmall={true}
        />
      </div>

      <p style={{ color: "var(--second-color)", marginTop: "0.5rem" }}>
        {devices.length} device{devices.length !== 1 ? "s" : ""} connected
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginTop: "1rem",
        }}
      >
        {devices.map((device) => (
          <DeviceCard
            key={device.id}
            id={device.id}
            name={device.device_info?.name || ""}
            os={device.device_info?.os || "Unknown OS"}
            platform={device.device_info?.platform || "Unknown platform"}
            isActive={device.is_active}
            createdAt={device.created_at}
            onClick={() =>
              navigate(getPath(ConfigRoutes.DEVICE_DETAIL, { id: device.id }))
            }
          />
        ))}
      </div>
    </div>
  );
};
